import { HttpErrorResponse, HttpEvent, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';

import { classifyHttpFailure } from '../observability/error-classification';
import { urlTemplate } from '../observability/url-template';
import { DatadogLogger } from '../vendor/datadog/datadog-logger';

/**
 * Logs every HTTP failure to Datadog, classified by status. Registered *outside*
 * `capacitorHttpInterceptorFn` so native failures are logged too. Timing lives in `httpLatencyInterceptor`.
 */
export function httpErrorInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const datadogLogger = inject(DatadogLogger);
  const requestId = request.headers.get('x-ninety-request-id');
  const startedAt = performance.now();

  return next(request).pipe(
    catchError((caughtError: unknown) => {
      const durationMs = Math.round(performance.now() - startedAt);

      if (!(caughtError instanceof HttpErrorResponse)) {
        // Nothing HTTP-shaped to read a status/body from — log what we have and pass it through as-is.
        const { level, reason } = classifyHttpFailure(undefined);
        datadogLogger[level]({
          message: 'http_request_error',
          error: caughtError,
          data: { url: request.url, requestId, durationMs, reason },
        });
        return throwError(() => caughtError);
      }

      const { errorMessage = null } = parseErrorBody(caughtError.error?.body);
      const { level, reason } = classifyHttpFailure(caughtError.status);
      const errorId = caughtError.error?.errorId;
      const serverMessage = caughtError.error?.message || caughtError.error?.errorMessage || errorMessage;

      datadogLogger[level]({
        message: 'http_request_error',
        // Datadog groups on `error.message`, and a value failing its `instanceof Error` check is
        // stringified whole — an HttpErrorResponse would ship its headers, body and query string as
        // one per-request signature. The route facet matches `httpLatencyInterceptor`'s, so latency
        // and failures for an endpoint join on the same string.
        error: new Error(`http_request_error ${caughtError.status} ${request.method} ${urlTemplate(request.url)}`),
        data: {
          url: caughtError.url,
          requestId,
          status: caughtError.status,
          reason,
          durationMs,
          ...(serverMessage ? { errorMessage: serverMessage } : {}),
          ...(errorId ? { errorId } : {}),
        },
      });

      // HttpErrorResponse doesn't extend Error and defines no toString of its own, so it falls through
      // to Object.prototype's "[object Object]" — attach a real one. Defined rather than assigned:
      // an own enumerable toString would be copied by every downstream spread, and each copy would
      // retain this closure over the full response body.
      Object.defineProperty(caughtError, 'toString', {
        value: () => `${serverMessage || caughtError.statusText || ''}`,
        enumerable: false,
        configurable: true,
        writable: true,
      });

      return throwError(() => caughtError);
    })
  );
}

function parseErrorBody(body: unknown): { errorMessage?: string | null } {
  if (!body || typeof body !== 'string') {
    return {};
  }

  try {
    const parsed = JSON.parse(body);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}
